import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, X, Sparkles, MapPin, Target, Loader2, RefreshCw, Shield, EyeOff,
} from "lucide-react";
import { useAuth } from "../../lib/auth";

/* Source icon lookup — tiny inline icon with a tooltip on hover.
   Keeping two separate icons (MapPin for local pack, Target for keyword
   overlap) makes "appears in both" readable at a glance. */
function SourceIcons({ sources }) {
  const hasLocal = sources.includes("local_pack");
  const hasOverlap = sources.includes("keyword_overlap");
  return (
    <span className="inline-flex items-center gap-0.5">
      {hasLocal && (
        <span title="Appears in your Local Pack snapshots" className="text-[#2c7a7b]">
          <MapPin className="h-3 w-3" strokeWidth={2.4} />
        </span>
      )}
      {hasOverlap && (
        <span title="Ranks for keywords you also rank for" className="text-[#2c7a7b]">
          <Target className="h-3 w-3" strokeWidth={2.4} />
        </span>
      )}
    </span>
  );
}

function MetricChip({ label, value }) {
  if (value == null) return null;
  return (
    <span className="text-[10px] text-[#718096] font-mono-tight whitespace-nowrap">
      {label} <span className="text-[#4a5568] font-semibold">{value}</span>
    </span>
  );
}

export default function CompetitorSuggestions({ client, onAccept, reloadClient }) {
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [acting, setActing] = useState({}); // domain -> boolean
  // Which row has its dismiss-menu expanded. Keeps "Block globally" one click
  // away without putting it as a top-level button on every chip (noisy).
  const [menuOpen, setMenuOpen] = useState(null);

  const load = async (force = false) => {
    if (!client?.id) return;
    if (force) setRefreshing(true);
    else setLoading(true);
    try {
      const { data } = await api.get(`/clients/${client.id}/competitors/suggestions`, {
        params: force ? { force_refresh: true } : {},
      });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load suggestions");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [client?.id]);

  // Close the dismiss menu when clicking anywhere outside it.
  useEffect(() => {
    if (menuOpen == null) return;
    const close = () => setMenuOpen(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [menuOpen]);

  const accept = async (domain) => {
    setActing((p) => ({ ...p, [domain]: true }));
    try {
      const existing = client.competitors || [];
      if (existing.map((c) => c.toLowerCase()).includes(domain.toLowerCase())) {
        toast.info(`${domain} is already in your competitors list.`);
      } else {
        await api.patch(`/clients/${client.id}`, { competitors: [...existing, domain] });
        toast.success(`Added ${domain} to competitors.`);
        onAccept?.(domain);
        reloadClient?.();
      }
      // Optimistically hide the accepted row.
      setData((d) => d ? { ...d, items: d.items.filter((i) => i.domain !== domain) } : d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add competitor");
    } finally {
      setActing((p) => ({ ...p, [domain]: false }));
    }
  };

  const dismiss = async (domain, scope) => {
    setActing((p) => ({ ...p, [domain]: true }));
    setMenuOpen(null);
    try {
      await api.post(`/clients/${client.id}/competitors/suggestions/dismiss`, { domain, scope });
      setData((d) => d ? { ...d, items: d.items.filter((i) => i.domain !== domain) } : d);
      if (scope === "global") {
        toast.success(`${domain} blocked across all clients.`);
      } else {
        toast.success(`${domain} dismissed for this client.`);
      }
      if (scope === "client") reloadClient?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't dismiss");
    } finally {
      setActing((p) => ({ ...p, [domain]: false }));
    }
  };

  if (loading) {
    return (
      <div className="card-soft p-5" data-testid="competitor-suggestions-loading">
        <div className="flex items-center gap-2 text-[11px] text-[#a0aec0]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Finding competitors…
        </div>
      </div>
    );
  }

  const items = data?.items || [];

  // Empty state — don't render the card at all for national clients with zero
  // suggestions once the user has dismissed everything. Keeps the tab tidy.
  if (items.length === 0 && !data?.overlap_error) {
    return null;
  }

  return (
    <div className="card-soft p-5 space-y-3" data-testid="competitor-suggestions-card">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Suggested competitors
          </div>
          <p className="text-xs text-[#718096] mt-0.5">
            Discovered from your Local Pack snapshots and your keyword-overlap profile.
            {" "}Tap <Plus className="inline h-3 w-3 -mt-0.5" /> to track, <X className="inline h-3 w-3 -mt-0.5" /> to dismiss.
          </p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing || client.frozen}
          data-testid="suggestions-refresh"
          title={client.frozen ? "Client is frozen — unfreeze to pull new data" : "Refresh keyword overlap (7-day cache, costs 1 DataForSEO call)"}
          className="btn-ghost inline-flex items-center gap-2 text-[11px]"
        >
          {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Refresh
        </button>
      </div>

      {data?.overlap_error && (
        <div className="rounded-xl bg-[#fffaf0] border border-[#feebc8] text-[#744210] text-[11px] px-3 py-2">
          Keyword overlap unavailable: {data.overlap_error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        {items.map((it) => {
          const busy = !!acting[it.domain];
          const isBoth = it.sources.length > 1;
          return (
            <div
              key={it.domain}
              data-testid={`suggestion-${it.domain}`}
              className={`group inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 bg-white transition-colors ${
                isBoth ? "border-[#4fd1c5] shadow-[0_0_0_1px_rgba(79,209,197,0.08)]" : "border-[#e2e8f0] hover:border-[#cbd5e0]"
              }`}
            >
              <SourceIcons sources={it.sources} />
              <span className="text-xs font-mono-tight text-[#1a202c]" title={it.title || it.domain}>
                {it.domain}
              </span>
              {it.intersections != null && (
                <MetricChip label="kw" value={it.intersections} />
              )}
              {it.local_pack_appearances != null && (
                <MetricChip label="pack" value={it.local_pack_appearances} />
              )}
              <div className="flex items-center gap-1 ml-1">
                <button
                  onClick={() => accept(it.domain)}
                  disabled={busy}
                  data-testid={`suggestion-accept-${it.domain}`}
                  title="Add to competitors"
                  className="h-5 w-5 rounded-full bg-[#4fd1c5]/10 hover:bg-[#4fd1c5] hover:text-white text-[#2c7a7b] inline-flex items-center justify-center transition-colors disabled:opacity-40"
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" strokeWidth={2.6} />}
                </button>
                {/* Dismiss button with inline menu for scope choice. */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setMenuOpen(menuOpen === it.domain ? null : it.domain)}
                    disabled={busy}
                    data-testid={`suggestion-dismiss-${it.domain}`}
                    title="Dismiss"
                    className="h-5 w-5 rounded-full bg-[#f7fafc] hover:bg-[#edf2f7] text-[#718096] inline-flex items-center justify-center transition-colors disabled:opacity-40"
                  >
                    <X className="h-3 w-3" strokeWidth={2.6} />
                  </button>
                  {menuOpen === it.domain && (
                    <div
                      className="absolute right-0 top-6 z-20 min-w-[220px] rounded-xl border border-[#e2e8f0] bg-white shadow-lg overflow-hidden"
                      data-testid={`suggestion-dismiss-menu-${it.domain}`}
                    >
                      <button
                        onClick={() => dismiss(it.domain, "client")}
                        data-testid={`suggestion-dismiss-client-${it.domain}`}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-[#f7fafc] inline-flex items-center gap-2 text-[#1a202c]"
                      >
                        <EyeOff className="h-3 w-3 text-[#718096]" />
                        Dismiss for this client
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => dismiss(it.domain, "global")}
                          data-testid={`suggestion-dismiss-global-${it.domain}`}
                          className="w-full px-3 py-2 text-left text-xs hover:bg-[#fff5f5] inline-flex items-center gap-2 text-[#c53030] border-t border-[#edf2f7]"
                        >
                          <Shield className="h-3 w-3" />
                          Block across all clients
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {data?.overlap_from_cache && (
        <div className="text-[10px] text-[#a0aec0] font-mono-tight pt-1">
          Keyword overlap cached · click Refresh for a live DataForSEO re-run.
        </div>
      )}
    </div>
  );
}
